import { LinkedAccount } from '../../axios/accountSwitches';

export interface SwitcherEntry extends LinkedAccount {
  /** What the row reads. Never empty. */
  label: string;
  /** The account id, only when the name is not enough to tell it apart. */
  hint: number | null;
}

/**
 * The selector rows, resolved so that each one identifies its own account.
 *
 * Somebody else chose the account name and it does not always serve: it can
 * come back empty, and nothing stops two accounts from sharing one. When that
 * happens the list stops being choosable and the person lands in the wrong
 * workspace.
 *
 * The id is added ONLY where it is needed. Putting it on every row would be
 * noise for the normal case, where the names already tell them apart; appearing
 * only on the ones that clash, the number says something: these two share a name.
 *
 * The replacement for an empty name already carries the id, so two unnamed
 * accounts never clash with each other either.
 */
export function buildSwitcherEntries(
  accounts: LinkedAccount[],
  unnamedLabel: (accountId: number) => string,
): SwitcherEntry[] {
  const labelled = accounts.map((account) => ({
    ...account,
    label: account.account_name?.trim() || unnamedLabel(account.account_id),
  }));

  const timesUsed = new Map<string, number>();
  labelled.forEach(({ label }) => timesUsed.set(label, (timesUsed.get(label) ?? 0) + 1));

  return labelled.map((entry) => ({
    ...entry,
    hint: (timesUsed.get(entry.label) ?? 0) > 1 ? entry.account_id : null,
  }));
}

/** How many accounts the user menu lists before it defers to the full list. */
export const MENU_ACCOUNT_LIMIT = 5;

/**
 * Folds case and accents, so what is typed reaches what is shown.
 *
 * The order already uses `localeCompare`, which puts `Máquinas` where a reader
 * looks for it. Comparing the search raw undid that: the row sorted where they
 * expected and then vanished when they typed `maquinas`, and past
 * MENU_ACCOUNT_LIMIT the search is the only way to that row.
 */
const fold = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    // The combining marks NFD splits off. Written as a range rather than
    // `\p{Diacritic}`, which needs a newer target than this project compiles to.
    .replace(/[\u0300-\u036f]/g, '');

/** Matches the name the row shows, and the id when that is what is visible. */
export function matchesSearch(entry: SwitcherEntry, term: string) {
  const needle = fold(term.trim());
  if (!needle) return true;

  return fold(entry.label).includes(needle) || String(entry.account_id).includes(needle);
}

interface VisibleEntriesOptions {
  /** Cuts the list after sorting, for the menu. Undefined keeps every match. */
  limit?: number;
  /** Kept at the top of the matches, so the cut cannot hide the current account. */
  pinnedUserId?: number | null;
}

/**
 * The rows to render: the matches for the search, in the order they are listed.
 *
 * Alphabetical and not in arrival order. The endpoint returns whatever order the
 * query produced, so the same account lands in a different place on every load
 * and cannot be found twice in the same spot. `localeCompare` puts accented
 * names where a Spanish or Portuguese reader looks for them.
 *
 * `pinnedUserId` exists for the menu, which shows only `limit` of N accounts:
 * plain alphabetical order would drop the current account out of view whenever
 * its name sorts late, and the menu would stop answering "where am I?". The
 * full list has no cut and needs no pin.
 *
 * The pin orders the matches, it does not add to them: searching for a name
 * shows the accounts with that name, never the current one on top of them.
 */
export function visibleEntries(
  entries: SwitcherEntry[],
  search: string,
  { limit, pinnedUserId }: VisibleEntriesOptions = {},
): SwitcherEntry[] {
  const matching = entries
    .filter((entry) => matchesSearch(entry, search))
    .sort((a, b) => {
      if (pinnedUserId !== null && pinnedUserId !== undefined) {
        if (a.user_id === pinnedUserId) return -1;
        if (b.user_id === pinnedUserId) return 1;
      }
      return a.label.localeCompare(b.label);
    });

  return limit === undefined ? matching : matching.slice(0, limit);
}

/**
 * Whether the menu needs the search field and the link to the full list.
 *
 * At or below the limit every account already fits in the menu: nothing is
 * hidden, so there is nothing to search for and nowhere else to go.
 */
export function needsFullList(total: number) {
  return total > MENU_ACCOUNT_LIMIT;
}
